import { Alert, Box, Flex, Heading, Select, Spinner, Text } from '@nrx/ui';
import type { JSX } from 'react';

import { GlobeIcon } from './globe-icon.js';
import type { GroupConsultEvent } from './group-consult-calendar.js';
import { GroupConsultListView } from './group-consult-list-view.js';
import { GroupConsultMonthView } from './group-consult-month-view.js';
import { GroupConsultViewToggle } from './group-consult-view-toggle.js';
import { TIMEZONE_OPTIONS } from './timezone-options.js';
import type { GroupConsultsState } from './use-group-consults.js';

const TIMEZONE_FIELD_ID = 'group-consult-timezone';

type GroupConsultTabProps = {
  group: GroupConsultsState;
  /** Registration needs a session JWT; without one the cards are read-only. */
  showRegisterButton: boolean;
  registerLoading: boolean;
  onRegisterClick: (event: GroupConsultEvent) => void;
};

/**
 * Group-consult tab body: consent copy, list/month toggle, the sessions themselves, timezone.
 *
 * @remarks The timezone control is now `Select` with a real `<label for>`. It was an unlabelled
 * native `<select>` carrying an inline `backgroundImage` data-URI for its chevron — both a W4
 * violation (no label) and a W2 one (a colour literal in a component). `.select` already ships
 * that chevron from a theme token, so the inline style is gone. The visible "Timezone" label is
 * new copy; `getByRole('combobox')` is unaffected and stays unique on this page.
 *
 * Loading and error states become `Spinner` (`role="status"`) and `Alert tone="danger"`
 * (`role="alert"`). "Loading sessions..." keeps its exact spelling — `waitForGroupSessionsLoaded`
 * in the page spec polls `queryByText` on that literal.
 */
export function GroupConsultTab({
  group,
  showRegisterButton,
  registerLoading,
  onRegisterClick,
}: GroupConsultTabProps): JSX.Element {
  const {
    events,
    webinarsStatus,
    webinarsError,
    displayTimeZone,
    setDisplayTimeZone,
    groupViewMode,
    setGroupViewMode,
    visibleGroupMonth,
    setVisibleGroupMonth,
    selectedGroupDayKey,
    setSelectedGroupDayKey,
    groupDayBuckets,
  } = group;

  return (
    <>
      <Heading as="h3" size="6" className="consult-section-title consult-section-title--start">
        <span className="consult-accent">Group Consult Calendar</span>
      </Heading>
      <Text as="p" color="muted" className="consult-consent">
        This is a group consult and by registering for it, you are consenting to participate in a
        group forum where PHI could be disclosed to other participants.
      </Text>

      <GroupConsultViewToggle
        viewMode={groupViewMode}
        onViewModeChange={(viewMode) => {
          setGroupViewMode(viewMode);
          if (viewMode !== 'month') {
            setSelectedGroupDayKey(null);
          }
        }}
      />

      {/* Group consult sessions list */}
      <Box className="consult-sessions">
        <Box className="consult-sessions__body">
          {webinarsStatus === 'loading' ? (
            <Flex align="center" justify="center" className="consult-sessions__state">
              <Spinner message="Loading sessions..." />
            </Flex>
          ) : webinarsStatus === 'error' && webinarsError ? (
            <Flex align="center" justify="center" className="consult-sessions__state">
              <Alert tone="danger">{webinarsError}</Alert>
            </Flex>
          ) : events.length === 0 ? (
            <Flex align="center" justify="center" className="consult-sessions__state">
              <Text as="p" color="muted">
                No upcoming events
              </Text>
            </Flex>
          ) : groupViewMode === 'month' ? (
            <Box className="consult-sessions__content">
              <GroupConsultMonthView
                dayBuckets={groupDayBuckets}
                selectedDayKey={selectedGroupDayKey}
                visibleMonth={visibleGroupMonth}
                onSelectDayKey={setSelectedGroupDayKey}
                onVisibleMonthChange={(month) => {
                  setVisibleGroupMonth(month);
                  setSelectedGroupDayKey(null);
                }}
              />
            </Box>
          ) : (
            <Box className="consult-sessions__content">
              <GroupConsultListView
                events={events}
                timeZone={displayTimeZone}
                showRegisterButton={showRegisterButton}
                registerLoading={registerLoading}
                onRegisterClick={onRegisterClick}
              />
            </Box>
          )}
        </Box>

        {/* Timezone selector — below the cards so it does not overlap */}
        <Flex align="center" gap="2" className="consult-timezone">
          <GlobeIcon className="consult-timezone__icon" />
          <label htmlFor={TIMEZONE_FIELD_ID} className="consult-timezone__label">
            <Text size="2" color="muted">
              Timezone
            </Text>
          </label>
          <Select
            id={TIMEZONE_FIELD_ID}
            value={displayTimeZone}
            onChange={setDisplayTimeZone}
            className="consult-timezone__select"
          >
            {TIMEZONE_OPTIONS.map((tz) => (
              <option key={tz} value={tz}>
                {tz}
              </option>
            ))}
          </Select>
        </Flex>
      </Box>
    </>
  );
}
